// API client for backend communication
// API client for backend communication

import { 
  ChatRequest, 
  ChatStreamEvent, 
  Conversation, 
  Message, 
  FileUploadResponse, 
  HealthResponse, 
  AdminStats, 
  AuthConfig,
  User,
  ApiError, 
  TemplateItem,
  TemplateListResponse
} from './types'
import type {
  OutlookSummarizeRequest,
  OutlookSummarizeResponse,
  OutlookDraftReplyRequest,
  OutlookDraftReplyResponse,
  OutlookClientEventRequest,
} from '@/addins/outlook/types'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'

/**
 * Custom error class for token expiration
 * Used to distinguish token expiration from other errors
 */
export class TokenExpiredError extends Error {
  constructor(message = 'Token expired') {
    super(message)
    this.name = 'TokenExpiredError'
  }
}

export class NetworkRequestError extends Error {
  constructor(message = 'Network request failed') {
    super(message)
    this.name = 'NetworkRequestError'
  }
}

class ApiClient {
  private baseUrl: string
  private authToken?: string

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl
  }

  private normalizeHeaders(input?: HeadersInit): Record<string, string> {
    if (!input) return {}
    const out: Record<string, string> = {}
    if (input instanceof Headers) {
      input.forEach((value, key) => {
        out[key] = value
      })
      return out
    }
    if (Array.isArray(input)) {
      for (const [key, value] of input) out[key] = value
      return out
    }
    // Plain object
    return { ...(input as Record<string, string>) }
  }

  setAuthToken(token: string) {
    this.authToken = token
  }

  getAuthToken(): string | undefined {
    return this.authToken
  }

  clearAuthToken() {
    this.authToken = undefined
  }

  private async request<T>(
    endpoint: string, 
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`
    
    const extraHeaders = this.normalizeHeaders(options.headers)
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...extraHeaders,
    }

    if (this.authToken) {
      headers['Authorization'] = `Bearer ${this.authToken}`
    }

    let response: Response
    try {
      response = await fetch(url, {
        ...options,
        headers,
      })
    } catch (error) {
      // Do not treat network/CORS failures as token expiration.
      // These errors can be transient and should not force logout/login redirects.
      if (error instanceof TypeError && error.message.includes('fetch')) {
        console.log('[API] Network/CORS error detected in request()')
        throw new NetworkRequestError('Network error. Please check your connection and try again.')
      }
      throw error
    }

    if (!response.ok) {
      // Handle 401 Unauthorized (token expired)
      if (response.status === 401) {
        console.log('[API] 401 Unauthorized detected in request(), throwing TokenExpiredError')
        // Clear auth token
        this.clearAuthToken()
        // Throw custom error for token expiration
        const error = new TokenExpiredError('Your session has expired. Please login again.')
        console.log('[API] TokenExpiredError created:', error)
        throw error
      }
      
      let errorDetail = `HTTP ${response.status}: ${response.statusText}`
      try {
        // Read response as text first to avoid issues with empty body
        const text = await response.text()
        if (text) {
          const errorData = JSON.parse(text)
          // FastAPI returns {detail: "message"} for HTTPException
          errorDetail = errorData.detail || errorData.error || errorDetail
        }
      } catch (e) {
        // If JSON parse fails, try to use response status text
        console.error('Failed to parse error response:', e)
      }
      throw new Error(errorDetail)
    }

    // Handle empty or non-JSON responses (e.g., 204 No Content)
    if (response.status === 204 || response.status === 205) {
      return undefined as unknown as T
    }
    const contentType = response.headers.get('content-type') || ''
    if (!contentType.includes('application/json')) {
      // Try best-effort parse; if empty, return undefined
      const text = await response.text().catch(() => '')
      return (text ? JSON.parse(text) : undefined) as unknown as T
    }
    // Safe JSON parse with empty-body guard
    const text = await response.text()
    return (text ? JSON.parse(text) : undefined) as unknown as T
  }

  private async requestStream(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<{ body: ReadableStream<Uint8Array>, headers: Headers }> {
    const url = `${this.baseUrl}${endpoint}`
    
    const extraHeaders = this.normalizeHeaders(options.headers)
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Accept': 'text/event-stream',
      ...extraHeaders,
    }

    if (this.authToken) {
      headers['Authorization'] = `Bearer ${this.authToken}`
    }

    let response: Response
    try {
      response = await fetch(url, {
        ...options,
        headers,
      })
    } catch (error) {
      // Do not treat network/CORS failures as token expiration.
      if (error instanceof TypeError && error.message.includes('fetch')) {
        console.log('[API] Network/CORS error detected in requestStream()')
        throw new NetworkRequestError('Network error. Please check your connection and try again.')
      }
      throw error
    }

    if (!response.ok) {
      // Handle 401 Unauthorized (token expired)
      if (response.status === 401) {
        // Clear auth token
        this.clearAuthToken()
        // Throw custom error for token expiration
        throw new TokenExpiredError('Your session has expired. Please login again.')
      }
      
      const error: ApiError = await response.json().catch(() => ({
        error: 'Network error',
        detail: `HTTP ${response.status}: ${response.statusText}`
      }))
      throw new Error(error.detail || error.error || 'Request failed')
    }

    return { body: response.body!, headers: response.headers }
  }

  // Health check
  async getHealth(): Promise<HealthResponse> {
    return this.request<HealthResponse>('/api/health')
  }

  // Authentication
  async getAuthConfig(): Promise<AuthConfig> {
    return this.request<AuthConfig>('/api/auth/config')
  }

  async getCurrentUser(): Promise<User> {
    return this.request<User>('/api/auth/me')
  }

  // Outlook add-in endpoints
  async outlookSummarize(payload: OutlookSummarizeRequest): Promise<OutlookSummarizeResponse> {
    return this.request<OutlookSummarizeResponse>('/api/outlook/summarize', {
      method: 'POST',
      headers: { 'X-Client': 'outlook_addin' },
      body: JSON.stringify(payload),
    })
  }

  async outlookDraftReply(payload: OutlookDraftReplyRequest): Promise<OutlookDraftReplyResponse> {
    return this.request<OutlookDraftReplyResponse>('/api/outlook/draft-reply', {
      method: 'POST',
      headers: { 'X-Client': 'outlook_addin' },
      body: JSON.stringify(payload),
    })
  }

  async outlookEvent(payload: OutlookClientEventRequest): Promise<void> {
    await this.request('/api/outlook/event', {
      method: 'POST',
      headers: { 'X-Client': 'outlook_addin' },
      body: JSON.stringify(payload),
    })
  }

  // Conversations
  async getConversations(skip = 0, limit = 50): Promise<Conversation[]> {
    return this.request<Conversation[]>(`/api/conversations?skip=${skip}&limit=${limit}`)
  }

  async getConversation(id: number): Promise<Conversation> {
    return this.request<Conversation>(`/api/conversations/${id}`)
  }

  async getAvailableModels(): Promise<{ models: Array<{ id: string; label: string; provider: string; enabled: boolean }> }> {
    return this.request<{ models: Array<{ id: string; label: string; provider: string; enabled: boolean }> }>(
      '/api/models',
      { method: 'GET' }
    )
  }

  async updateConversationModel(conversationId: number, modelId: string): Promise<Conversation> {
    return this.request<Conversation>(
      `/api/conversations/${conversationId}/model`,
      { 
        method: 'PUT',
        body: JSON.stringify({ model_id: modelId })
      }
    )
  }

  async getPopularModels(startDate?: string, endDate?: string): Promise<{
    start_date: string
    end_date: string
    models: Array<{ model_id: string; model_label: string; usage_count: number }>
    total_messages: number
  }> {
    const params = new URLSearchParams()
    if (startDate) params.append('start_date', startDate)
    if (endDate) params.append('end_date', endDate)
    const queryString = params.toString()
    return this.request<{
      start_date: string
      end_date: string
      models: Array<{ model_id: string; model_label: string; usage_count: number }>
      total_messages: number
    }>(`/api/admin/models/popular${queryString ? `?${queryString}` : ''}`, { method: 'GET' })
  }

  async updateConversation(id: number, data: Partial<Conversation>): Promise<Conversation> {
    return this.request<Conversation>(`/api/conversations/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async deleteConversation(id: number): Promise<void> {
    await this.request(`/api/conversations/${id}`, {
      method: 'DELETE',
    })
  }

  // Chat streaming
  async streamChat(
    request: ChatRequest,
    onEvent: (event: ChatStreamEvent) => void,
    abortSignal?: AbortSignal
  ): Promise<string | undefined> {
    const { body, headers } = await this.requestStream('/api/chat/stream', {
      method: 'POST',
      body: JSON.stringify(request),
      signal: abortSignal,
    })

    // Read conversation id from response headers if present
    // console.log('[API] Response headers:', Array.from(headers.entries()))
    const conversationId = headers.get('X-Conversation-ID') || undefined
    // console.log('[API] X-Conversation-ID header value:', conversationId)

    const reader = body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''

    try {
      while (true) {
        // Check if aborted
        if (abortSignal?.aborted) {
          console.log('[API] Stream aborted by user')
          break
        }
        
        const { value, done } = await reader.read()
        if (done) break
        
        // Check again after read
        if (abortSignal?.aborted) {
          console.log('[API] Stream aborted after read')
          break
        }

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() || ''

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const event: ChatStreamEvent = JSON.parse(line.slice(6))
              onEvent(event)
            } catch (e) {
              console.warn('Failed to parse SSE event:', e)
            }
          }
        }
      }
    } finally {
      reader.releaseLock()
    }

    return conversationId
  }

  // File upload
  async uploadFile(file: File): Promise<FileUploadResponse> {
    const formData = new FormData()
    formData.append('file', file)

    const headers: HeadersInit = {}
    if (this.authToken) {
      headers.Authorization = `Bearer ${this.authToken}`
    }

    const response = await fetch(`${this.baseUrl}/api/files/upload`, {
      method: 'POST',
      headers,
      body: formData,
    })

    if (!response.ok) {
      // Handle 401 Unauthorized (token expired)
      if (response.status === 401) {
        // Clear auth token
        this.clearAuthToken()
        // Throw custom error for token expiration
        throw new TokenExpiredError('Your session has expired. Please login again.')
      }
      
      const error: ApiError = await response.json().catch(() => ({
        error: 'Upload failed',
        detail: `HTTP ${response.status}: ${response.statusText}`
      }))
      throw new Error(error.detail || error.error || 'Upload failed')
    }

    return response.json()
  }

  async getFile(id: string): Promise<Blob> {
    const headers: HeadersInit = {}
    if (this.authToken) {
      headers.Authorization = `Bearer ${this.authToken}`
    }

    const response = await fetch(`${this.baseUrl}/api/files/${id}`, {
      headers,
    })

    if (!response.ok) {
      // Handle 401 Unauthorized (token expired)
      if (response.status === 401) {
        // Clear auth token
        this.clearAuthToken()
        // Throw custom error for token expiration
        throw new TokenExpiredError('Your session has expired. Please login again.')
      }
      
      throw new Error(`Failed to get file: ${response.statusText}`)
    }

    return response.blob()
  }

  async getFileInfo(id: string): Promise<FileUploadResponse> {
    return this.request<FileUploadResponse>(`/api/files/${id}/info`)
  }

  async deleteFile(id: string): Promise<void> {
    await this.request(`/api/files/${id}`, {
      method: 'DELETE',
    })
  }

  async listFiles(skip = 0, limit = 50): Promise<{ files: FileUploadResponse[] }> {
    return this.request<{ files: FileUploadResponse[] }>(`/api/files?skip=${skip}&limit=${limit}`)
  }

  // Admin endpoints
  async getAdminStats(): Promise<AdminStats> {
    return this.request<AdminStats>('/api/admin/stats')
  }

  async getAdminUsers(skip = 0, limit = 150): Promise<{users: User[], total: number, skip: number, limit: number}> {
    return this.request<{users: User[], total: number, skip: number, limit: number}>(`/api/admin/users?skip=${skip}&limit=${limit}`)
  }

  async getAdminRecentSessions(limit = 200): Promise<{ sessions: any[]; limit: number }> {
    return this.request<{ sessions: any[]; limit: number }>(`/api/admin/sessions/recent?limit=${limit}`)
  }

  async updateUserRole(userId: number, accessLevel: string): Promise<User> {
    return this.request<User>(`/api/admin/users/${userId}/role`, {
      method: 'PUT',
      body: JSON.stringify({ access_level: accessLevel }),
    })
  }

  async toggleUserAdmin(userId: number): Promise<User> {
    return this.request<User>(`/api/admin/users/${userId}/toggle-admin`, {
      method: 'PUT',
    })
  }

  async toggleUserStatus(userId: number): Promise<User> {
    return this.request<User>(`/api/admin/users/${userId}/toggle-status`, {
      method: 'PUT',
    })
  }

  // Analytics endpoints
  async getAnalyticsOverview(): Promise<any> {
    return this.request(`/api/admin/analytics/overview`)
  }

  async getUserAnalytics(userId: number): Promise<any> {
    return this.request(`/api/admin/analytics/users/${userId}`)
  }

  async getUsageTrends(period: string = 'daily', days: number = 30): Promise<any> {
    return this.request(`/api/admin/analytics/usage-trends?period=${period}&days=${days}`)
  }

  async getCostAnalytics(days: number = 30): Promise<any> {
    return this.request(`/api/admin/analytics/costs?days=${days}`)
  }

  async getPerformanceAnalytics(days: number = 30): Promise<any> {
    return this.request(`/api/admin/analytics/performance?days=${days}`)
  }

  // Template library endpoints
  async listTemplates(scope: 'personal'|'public', query = '', page = 1, pageSize = 20, keywords?: string): Promise<TemplateListResponse> {
    const q = new URLSearchParams()
    q.set('scope', scope)
    if (query) q.set('query', query)
    if (keywords) q.set('keywords', keywords)
    q.set('page', String(page))
    q.set('page_size', String(pageSize))
    return this.request<TemplateListResponse>(`/api/templates?${q.toString()}`)
  }

  async getTemplate(id: number): Promise<TemplateItem> {
    return this.request<TemplateItem>(`/api/templates/${id}`)
  }

  async createTemplate(data: { type: 'prompt'|'persona', title: string, description?: string, content: string, visibility: 'personal'|'public', keywords?: string[] }): Promise<TemplateItem> {
    return this.request<TemplateItem>(`/api/templates`, {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  async updateTemplate(id: number, data: { title?: string, description?: string, content?: string, keywords?: string[] }): Promise<TemplateItem> {
    return this.request<TemplateItem>(`/api/templates/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    })
  }

  async deleteTemplate(id: number): Promise<void> {
    await this.request(`/api/templates/${id}`, { method: 'DELETE' })
  }

  async forkTemplate(id: number): Promise<TemplateItem> {
    return this.request<TemplateItem>(`/api/templates/${id}/fork`, { method: 'POST' })
  }

  async publishTemplate(id: number, data: { title?: string, description?: string, content?: string, keywords?: string[] }): Promise<TemplateItem> {
    return this.request<TemplateItem>(`/api/templates/${id}/publish-public`, { method: 'POST', body: JSON.stringify(data) })
  }

  async markTemplateUsed(id: number): Promise<void> {
    await this.request(`/api/templates/${id}/use`, { method: 'POST' })
  }

  async favoriteTemplate(id: number): Promise<void> {
    await this.request(`/api/templates/${id}/favorite`, { method: 'POST' })
  }

  async unfavoriteTemplate(id: number): Promise<void> {
    await this.request(`/api/templates/${id}/favorite`, { method: 'DELETE' })
  }
}

// Export singleton instance
export const apiClient = new ApiClient()

// Export class for testing
export { ApiClient }

